import toast from "react-hot-toast";
import { toastPresets } from "./toastPresets";

/**
 * Wraps an async function with toast and optional loader state.
 *
 * @param {Function} asyncFunc - Async function to execute
 * @param {Object} options
 * @param {string} [options.preset] - Preset name (e.g., 'add', 'update')
 * @param {Function} [options.setLoading] - Optional loading state setter
 * @param {string} [options.loadingMessage] - Custom loading message
 * @param {string} [options.successMessage] - Custom success message
 * @param {string} [options.errorMessage] - Custom error message
 * @param {Object} [options.toastOptions] - Toast customization (icon, position)
 * @param {number} [options.retry] - Retry attempts
 * @param {number} [options.retryDelay] - Delay between retries
 *
 * @returns {Promise<any>}
 */
export async function withToastAndLoader(
  asyncFunc,
  {
    preset = "default",
    setLoading,
    loadingMessage,
    successMessage,
    errorMessage,
    toastOptions = {},
    retry = 0,
    retryDelay = 1000,
  } = {}
) {
  const presetConfig = toastPresets[preset] || toastPresets.default;

  const finalLoading = loadingMessage || presetConfig.loadingMessage;
  const finalSuccess = successMessage || presetConfig.successMessage;
  const finalError = errorMessage || presetConfig.errorMessage;
  const finalOptions = { ...presetConfig.toastOptions, ...toastOptions };

  if (setLoading) setLoading(true);

  let attempt = 0;
  const toastId = toast.loading(finalLoading, { ...finalOptions });
  while (attempt <= retry) {
    try {
      const result = await asyncFunc();
      toast.success(finalSuccess, { id: toastId, ...finalOptions });
      return result;
    } catch (error) {
      attempt++;

      if (attempt > retry) {
        toast.error(finalError, { id: toastId, ...finalOptions });
        throw error;
      }

      if (retryDelay > 0) {
        await new Promise((res) => setTimeout(res, retryDelay));
      }
    } finally {
      if (setLoading) setLoading(false);
    }
  }
}
